import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { getDocs, collection } from "firebase/firestore";
import { db } from "../../firebase";

export default function DoctorSection() {
  const [doctors, setDoctors] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const [newDoctor, setNewDoctor] = useState({
    name: "",
    email: "",
    password: "",
    credentials: "",
    specialty: "",
    department: "",
    image: null, // File object
    description: "",
    role: "doctor", // default role
  });

  const fetchDoctors = async () => {
    try {
      const snapshot = await getDocs(collection(db, "users"));
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setDoctors(data.filter((d) => d.role === "doctor" || d.role === "staff"));
    } catch (err) {
      console.error("Error fetching doctors:", err);
    }
  };

  useEffect(() => {
    fetchDoctors();
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, []);

  const handleFileChange = (file) => {
    if (!file) {
      setNewDoctor({ ...newDoctor, image: null });
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
        setImagePreview(null);
      }
      return;
    }
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
    setNewDoctor({ ...newDoctor, image: file });
  };

  const handleAddDoctor = async (e) => {
    e.preventDefault();
    // Basic validation
    if (!newDoctor.name || !newDoctor.email || !newDoctor.password) {
      alert("Please fill Name, Email, and Password.");
      return;
    }
    // Doctor-specific validation
    if (
      newDoctor.role === "doctor" &&
      (!newDoctor.specialty || !newDoctor.credentials)
    ) {
      alert("Please fill Credentials and Specialty for doctors.");
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", newDoctor.name);
      formData.append("email", newDoctor.email);
      formData.append("password", newDoctor.password);
      formData.append("role", newDoctor.role);
      formData.append("description", newDoctor.description || "");
      if (newDoctor.role === "doctor") {
        formData.append("credentials", newDoctor.credentials);
        formData.append("specialty", newDoctor.specialty);
      }
      if (newDoctor.role === "staff") {
        formData.append("department", newDoctor.department || "");
      }
      if (newDoctor.image) formData.append("image", newDoctor.image);

      const res = await fetch("https://vpjc-api-805872224468.asia-south1.run.app/api/add-user", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        let errText = await res.text();
        throw new Error(errText || "Failed to add user");
      }

      const data = await res.json();
      console.log("User created:", data);

      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
        setImagePreview(null);
      }
      setNewDoctor({
        name: "",
        email: "",
        password: "",
        credentials: "",
        specialty: "",
        department: "",
        image: null,
        description: "",
        role: "doctor",
      });
      setShowDialog(false);
      fetchDoctors();
    } catch (err) {
      console.error("Error adding user:", err);
      alert("Failed to add user: " + (err.message || err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-[#204080]">Doctors</h2>
          <p className="text-gray-600 text-sm">
            Manage doctors profiles
          </p>
        </div>
        <Button onClick={() => setShowDialog(true)} className="bg-blue-600">
          + Add Doctor
        </Button>
      </div>

      {/* Doctors List */}
      {/* Doctors List */}
      {doctors.length === 0 ? (
        <p className="text-gray-600">No doctors found.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {doctors.map((d) => (
            <div
              key={d.id}
              className="bg-gradient-to-br from-blue-50 to-white border rounded-2xl shadow-md p-6 flex flex-col items-center text-center hover:shadow-lg transition max-w-xs mx-auto"
            >
              <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-blue-100 shadow">
                <img
                  src={d.image || "https://via.placeholder.com/150"}
                  alt={d.name}
                  className="w-full h-full object-cover"
                />
              </div>

              <h3 className="mt-4 text-lg font-bold text-gray-800">{d.name}</h3>

              {/* Role Badge */}
              <span
                className={`mt-2 inline-block text-xs px-3 py-1 rounded-full ${
                  d.role === "doctor"
                    ? "bg-blue-100 text-blue-700"
                    : "bg-green-100 text-green-700"
                }`}
              >
                {d.role.charAt(0).toUpperCase() + d.role.slice(1)}
              </span>

              {/* Doctor-specific info */}
              {d.role === "doctor" && (
                <>
                  <p className="text-sm text-blue-700 font-medium mt-1">
                    {d.credentials}
                  </p>
                  <span className="mt-1 inline-block bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full">
                    {d.specialty}
                  </span>
                </>
              )}

              {/* Staff-specific info */}
              {d.role === "staff" && d.department && (
                <span className="mt-1 inline-block bg-green-50 text-green-700 text-xs px-2 py-1 rounded-full">
                  {d.department}
                </span>
              )}

              {d.description && (
                <p className="text-sm text-gray-500 mt-3 line-clamp-3">
                  {d.description}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Doctor/Staff Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Doctor</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleAddDoctor} className="space-y-4">
            <input
              type="text"
              placeholder="Name"
              value={newDoctor.name}
              onChange={(e) =>
                setNewDoctor({ ...newDoctor, name: e.target.value })
              }
              className="border p-2 rounded w-full"
              required
            />
            <input
              type="email"
              placeholder="Email"
              value={newDoctor.email}
              onChange={(e) =>
                setNewDoctor({ ...newDoctor, email: e.target.value })
              }
              className="border p-2 rounded w-full"
              required
            />
            <input
              type="password"
              placeholder="Temporary Password"
              value={newDoctor.password}
              onChange={(e) =>
                setNewDoctor({ ...newDoctor, password: e.target.value })
              }
              className="border p-2 rounded w-full"
              required
            />

            <select
              value={newDoctor.role}
              onChange={(e) =>
                setNewDoctor({ ...newDoctor, role: e.target.value })
              }
              className="border p-2 rounded w-full"
            >
              <option value="doctor">Doctor</option>
              {/* <option value="staff">Staff</option> */}
            </select>

            {/* Doctor-only fields */}
            {newDoctor.role === "doctor" && (
              <>
                <input
                  type="text"
                  placeholder="Credentials (MPT, BPT, etc.)"
                  value={newDoctor.credentials}
                  onChange={(e) =>
                    setNewDoctor({ ...newDoctor, credentials: e.target.value })
                  }
                  className="border p-2 rounded w-full"
                  required
                />
                <input
                  type="text"
                  placeholder="Specialty"
                  value={newDoctor.specialty}
                  onChange={(e) =>
                    setNewDoctor({ ...newDoctor, specialty: e.target.value })
                  }
                  className="border p-2 rounded w-full"
                  required
                />
              </>
            )}

            {/* Staff-only fields */}
            {newDoctor.role === "staff" && (
              <input
                type="text"
                placeholder="Department / Role (Receptionist, Nurse, etc.)"
                value={newDoctor.department}
                onChange={(e) =>
                  setNewDoctor({ ...newDoctor, department: e.target.value })
                }
                className="border p-2 rounded w-full"
              />
            )}

            <textarea
              placeholder="Description (optional)"
              value={newDoctor.description}
              onChange={(e) =>
                setNewDoctor({ ...newDoctor, description: e.target.value })
              }
              className="border p-2 rounded w-full"
            />

            {/* Image upload */}
            <div className="space-y-2">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e.target.files[0])}
                className="border p-2 rounded w-full"
              />
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-24 h-24 rounded-full object-cover"
                />
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (imagePreview) URL.revokeObjectURL(imagePreview);
                  setImagePreview(null);
                  setNewDoctor({
                    name: "",
                    email: "",
                    password: "",
                    credentials: "",
                    specialty: "",
                    department: "",
                    image: null,
                    description: "",
                    role: "doctor",
                  });
                  setShowDialog(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Adding..." : "Add Doctor"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
